const WRAPPER_SELECTOR = '[data-component="product-reviews"]';
const LIST_SELECTOR = '[data-product-reviews-list]';
const PAGINATION_SELECTOR = '[data-product-reviews-pagination]';
const PAGE_BUTTON_SELECTOR = '[data-product-reviews-page]';
const OPEN_MODAL_SELECTOR = '[data-product-reviews-open-modal]';
const CLOSE_MODAL_SELECTOR = '[data-product-reviews-close-modal]';
const MODAL_SELECTOR = '[data-product-reviews-modal]';
const STAR_LINK_SELECTOR = '#respond p.stars a';
const STARS_SELECTOR = '#respond p.stars';

const parsePositiveInteger = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1) {
        return fallback;
    }

    return parsed;
};

const normalizeRatingValue = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1 || parsed > 5) {
        return 0;
    }

    return parsed;
};

const parseStarRatingFromClass = (starElement) => {
    if (!starElement) {
        return 0;
    }

    const starClass = Array.from(starElement.classList).find((className) => /^star-[1-5]$/.test(className));

    if (!starClass) {
        return 0;
    }

    return normalizeRatingValue(starClass.replace('star-', ''));
};

const applyStarsRatingState = (starsWrapper, ratingValue) => {
    if (!starsWrapper) {
        return;
    }

    const normalizedRating = normalizeRatingValue(ratingValue);
    const starLinks = starsWrapper.querySelectorAll('a');

    if (normalizedRating > 0) {
        starsWrapper.dataset.rating = String(normalizedRating);
    } else {
        delete starsWrapper.dataset.rating;
    }

    starsWrapper.classList.toggle('selected', normalizedRating > 0);

    starLinks.forEach((starLink) => {
        const starValue = parseStarRatingFromClass(starLink);
        const isActive = normalizedRating > 0 && starValue === normalizedRating;

        starLink.classList.toggle('active', isActive);
        starLink.setAttribute('aria-checked', isActive ? 'true' : 'false');
        starLink.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    if (normalizedRating === 0 && starLinks.length) {
        starLinks[0].setAttribute('tabindex', '0');
    }
};

const synchronizeComponentStars = (component) => {
    const starsWrappers = component.querySelectorAll(STARS_SELECTOR);

    starsWrappers.forEach((starsWrapper) => {
        const respond = starsWrapper.closest('#respond');
        const ratingSelect = respond ? respond.querySelector('#rating') : null;
        const ratingValue = ratingSelect ? ratingSelect.value : '';

        applyStarsRatingState(starsWrapper, ratingValue);
    });
};

const setLoadingState = (component, isLoading) => {
    component.classList.toggle('is-loading', isLoading);
    component.setAttribute('aria-busy', String(isLoading));
};

const fetchReviewsPage = async (component, pageNumber) => {
    if (!window.jsData || !window.jsData.ajaxUrl) {
        throw new Error('Missing jsData.ajaxUrl');
    }

    const productId = parsePositiveInteger(component.dataset.productId, 0);
    const action = component.dataset.action;
    const nonce = component.dataset.nonce || '';

    if (!productId || !action || !nonce) {
        throw new Error('Missing required payload values');
    }

    const payload = new URLSearchParams();
    payload.append('action', action);
    payload.append('product_id', String(productId));
    payload.append('page', String(pageNumber));
    payload.append('nonce', nonce);

    const response = await fetch(window.jsData.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: payload.toString(),
    });

    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
    }

    return response.json();
};

const replaceReviewsMarkup = (component, responseData) => {
    const list = component.querySelector(LIST_SELECTOR);
    const pagination = component.querySelector(PAGINATION_SELECTOR);

    if (list && typeof responseData.list_html === 'string') {
        list.innerHTML = responseData.list_html;
    }

    if (pagination && typeof responseData.pagination_html === 'string') {
        pagination.innerHTML = responseData.pagination_html;
    }

    const currentPage = parsePositiveInteger(responseData.current_page, 1);
    component.dataset.currentPage = String(currentPage);
};

const bindReviewsPagination = (component) => {
    let isRequestPending = false;

    component.addEventListener('click', async (event) => {
        const target = event.target.closest(PAGE_BUTTON_SELECTOR);

        if (!target) {
            return;
        }

        event.preventDefault();

        if (target.hasAttribute('disabled') || isRequestPending) {
            return;
        }

        const nextPage = parsePositiveInteger(target.dataset.productReviewsPage, 0);
        const currentPage = parsePositiveInteger(component.dataset.currentPage, 1);

        if (!nextPage || nextPage === currentPage) {
            return;
        }

        isRequestPending = true;
        setLoadingState(component, true);

        try {
            const response = await fetchReviewsPage(component, nextPage);

            if (!response || response.success !== true || !response.data) {
                return;
            }

            replaceReviewsMarkup(component, response.data);
        } catch (error) {
            // Keep graceful degradation: on request failure the currently rendered
            // opinions remain visible and interactive.
            console.error(error);
        } finally {
            isRequestPending = false;
            setLoadingState(component, false);
        }
    });
};

const bindReviewsModal = (component) => {
    const modal = component.querySelector(MODAL_SELECTOR);
    if (!modal) {
        return;
    }

    const closeModal = (event) => {
        if (event) {
            event.preventDefault();
        }

        modal.classList.remove('open');
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflowY = '';
    };

    const openModal = (event) => {
        if (event) {
            event.preventDefault();
        }

        modal.classList.add('open');
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflowY = 'hidden';
    };

    const openButtons = component.querySelectorAll(OPEN_MODAL_SELECTOR);
    const closeButtons = component.querySelectorAll(CLOSE_MODAL_SELECTOR);
    const modalInner = modal.querySelector('.ProductReviewsModal__inner');

    openButtons.forEach((button) => {
        button.addEventListener('click', openModal);
    });

    closeButtons.forEach((button) => {
        button.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (event) => {
        const clickedInsideInner = modalInner && modalInner.contains(event.target);
        if (!clickedInsideInner) {
            closeModal(event);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('open')) {
            closeModal();
        }
    });
};

const bindReviewsRating = (component) => {
    component.addEventListener(
        'click',
        (event) => {
            const starLink = event.target.closest(STAR_LINK_SELECTOR);

            if (!starLink || !component.contains(starLink)) {
                return;
            }

            const starsWrapper = starLink.closest('p.stars');
            const ratingValue = parseStarRatingFromClass(starLink);

            if (!starsWrapper || ratingValue === 0) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const respond = starLink.closest('#respond');
            const ratingSelect = respond ? respond.querySelector('#rating') : null;

            if (ratingSelect) {
                ratingSelect.value = String(ratingValue);
            }

            applyStarsRatingState(starsWrapper, ratingValue);
        },
        true,
    );

    synchronizeComponentStars(component);

    if (typeof MutationObserver === 'undefined') {
        return;
    }

    const observer = new MutationObserver((mutations) => {
        const shouldSync = mutations.some((mutation) => mutation.type === 'childList' && mutation.addedNodes.length > 0);

        if (shouldSync) {
            synchronizeComponentStars(component);
        }
    });

    observer.observe(component, {
        childList: true,
        subtree: true,
    });
};

document.addEventListener('DOMContentLoaded', () => {
    const reviewComponents = document.querySelectorAll(WRAPPER_SELECTOR);

    reviewComponents.forEach((component) => {
        bindReviewsRating(component);
        bindReviewsPagination(component);
        bindReviewsModal(component);
    });
});
